import './style.scss';
import {useEffect} from 'react';
import {useLocationDetailsContext} from 'src/context/LocationDetailsContext';
import {LocationFooter} from './LocationFooter/LocationFooter';
import {LocationHeader} from './LocationHeader/LocationHeader';
import {LocationMain} from './LocationMain/LocationMain';
import {LocationAside} from './LocationAside/LocationAside';
import {BookingActions, useBookingContext} from 'src/context/BookingContext';
import {useCaregiverConsentCookie} from 'src/hooks/useCaregiverConsentCookie';
import {CookieConsentModal} from '../cookies/CookieConsent/CookieConsentModal';
import {AppointmentCategory, CaregiverObject, Location, ReasonFilterObject} from 'src/services/api/types/types';
import {VisitCategory} from '../main/components/PickVisitCategory';
import {useTranslation} from 'react-i18next';
import {injectCustomCSSLink} from 'src/lib/customcss/customcss';

export function LocationDetails() {
    const {t} = useTranslation();

    const {
        locationDataState: {loading, error, location, caregiver, visitReasons, appointmentCategories}
    } = useLocationDetailsContext();

    const {
        bookingState: {step},
        dispatch: bookingDispatch
    } = useBookingContext();

    const {hasChosen, setCookieConsent} = useCaregiverConsentCookie(
        caregiver?.id as number,
        caregiver?.cookiePolicyVersion as string
    );
    const stepClasses = step ? ` ${step}` : '';

    const findVisitReasons = (
        visitReasonCateogriesOnLocation: VisitCategory[],
        visitReasons: ReasonFilterObject[],
        appointmentCategories: Array<AppointmentCategory>,
        itemLimit = 50
    ) => {
        let servicesText: string[] = [];
        const possibleVisitReasons: VisitCategory[] = ['vaccination', 'travel', 'other'];
        possibleVisitReasons.forEach((visitReasonCategory: VisitCategory) => {
            const hasCategory = visitReasonCateogriesOnLocation?.filter((category) => category === visitReasonCategory);
            if (hasCategory.length > 0) {
                const appointmentCategoriesForReason = appointmentCategories.filter((category) =>
                    category.appointmentTypes.some((appointmentType) =>
                        appointmentType.visitReasonIds.some((reasonId) =>
                            visitReasons.some(
                                (visitReason) =>
                                    visitReason.category === visitReasonCategory && Number(visitReason.id) === reasonId
                            )
                        )
                    )
                );

                if (appointmentCategoriesForReason.length > 0) {
                    if (appointmentCategoriesForReason.length > itemLimit) {
                        appointmentCategoriesForReason.length = itemLimit; // Limit number of items
                    }
                    const serviceText = t(`meta.${visitReasonCategory}`);

                    const appointmentCategoriesForReasonNames = appointmentCategoriesForReason.map(
                        (category) => category.name
                    );

                    const categoryVisitReasonsText = appointmentCategoriesForReasonNames
                        .join(', ')
                        .replace(/,(?!.*,)/gim, ' ' + t('meta.and'));
                    const text = serviceText + ' (' + categoryVisitReasonsText + ')';
                    servicesText.push(text);
                }
            }
        });
        return servicesText;
    };

    useEffect(() => {
        if (loading || location === null) {
            return;
        }

        const getLocationServicesText = (
            visitReasons: Array<ReasonFilterObject>,
            appointmentCategories: Array<Array<AppointmentCategory>>
        ): string => {
            let services = '';

            services += ' ' + t('meta.clinic.visit_reason_booking_and_drop_in');

            const uniqueVisitReasonCategories = visitReasons.filter(
                (value, index, self) => index === self.findIndex((t) => t.category === value.category)
            );

            const appointmentCategoriesFlat = appointmentCategories.flat().filter((value, index, self) => {
                return self.findIndex((v) => v.name === value.name) === index;
            });

            if (uniqueVisitReasonCategories.length > 0) {
                let visitReasonCateogriesOnLocation = uniqueVisitReasonCategories?.map((reason) => reason.category);
                if (visitReasonCateogriesOnLocation.length > 0) {
                    const servicesText = findVisitReasons(
                        visitReasonCateogriesOnLocation,
                        visitReasons,
                        appointmentCategoriesFlat
                    );

                    let servicesTextCombined = servicesText
                        .join(', ')
                        .replace(/, ([^,]*)$/, ' ' + t('meta.and') + ' $1');

                    services = services += ' ' + t('meta.for') + ' ' + servicesTextCombined;
                }
            }
            if (services !== '') {
                services = services + '.';
            }
            return services;
        };

        const getLocationInfoText = (location: Location, caregiver: CaregiverObject | null): string => {
            let caregiverName = '';
            let city = '';

            const locationName = location.name;

            if (caregiver?.label !== undefined) {
                caregiverName += `${caregiver.label}, `;
            }

            if (location.city) {
                city = `, ${location.city}`;
            }

            return `${t('meta.clinic.welcome_to')} ${caregiverName}${locationName}${city}.`;
        };

        // Add metadata description text
        const generateDescriptionText = (
            location: Location,
            visitReasons: ReasonFilterObject[],
            appointmentCategories: Array<Array<AppointmentCategory>>
        ): string => {
            let descriptionText = '';

            // Add clinic related information
            descriptionText += getLocationInfoText(location, caregiver);

            // Add service related items
            descriptionText += getLocationServicesText(visitReasons, appointmentCategories);

            return descriptionText;
        };

        const descriptionElement = document.querySelector('meta[name=description]');
        if (descriptionElement) {
            const descriptionText = generateDescriptionText(location, visitReasons, appointmentCategories);
            descriptionElement?.setAttribute('content', descriptionText.replace(/(\r\n|\n|\r)/gm, ' '));
        }

        const titleElement = document.querySelector('title');
        if (titleElement) {
            titleElement.text = `${location.name} - MittVaccin.se`;
        }
    }, [location, caregiver, loading]);

    // TODO: Add GMT tracker info (currently dependent on clinic information) - MVJ-3891

    // Reset booking data
    useEffect(() => {
        if (location === null) {
            return;
        }

        if (location.externalBookingUrlType && location.externalBookingUrl) {
            // redirect to external booking
            window.location.href = location.externalBookingUrl;
        }

        bookingDispatch({type: BookingActions.RESET_BOOKING});
        bookingDispatch({type: BookingActions.RESET_BOOKING_ID});
    }, [location, bookingDispatch]);

    useEffect(() => {
        if (location === null || !location.stylesheetUrl) {
            return;
        }

        return injectCustomCSSLink(location.stylesheetUrl);
    }, [location]);

    return (
        <div className={`container${stepClasses}`} aria-busy={loading}>
            {caregiver && caregiver.cookiePolicyVersion && setCookieConsent !== undefined && (
                <CookieConsentModal caregiver={caregiver} hasChosen={hasChosen} setCookieConsent={setCookieConsent} />
            )}
            <LocationHeader />
            {!loading && !error && (
                <>
                    <LocationMain />
                    <LocationAside />
                </>
            )}

            {!loading && <LocationFooter />}
        </div>
    );
}
